import type { Comment } from '@atlas/api-contract';
import { MoreHorizontal, Pencil, Trash2 } from 'lucide-react';
import { useState } from 'react';

import { Avatar, AvatarFallback } from '@/components/ui/avatar';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { useCan } from '@/features/auth';
import { formatAbsoluteTime, formatRelativeTime } from '@/lib/format/date';
import { cn } from '@/lib/utils';

import { CommentComposer } from './comment-composer';
import { authorInitial } from './thread-display';
import { toUpdateCommentPayload } from './thread-schemas';
import { useUpdateComment } from './threads-queries';

export interface CommentItemProps {
  readonly comment: Comment;
  readonly versionId: string;
  /** The opening comment is styled as the thread's subject, not as a reply. */
  readonly isFirst: boolean;
  /** Opens the confirmation. Deleting is never done from here directly. */
  readonly onRequestDelete: (comment: Comment) => void;
}

/**
 * One comment: who said it, when, what, and whether it has been changed since.
 *
 * ============================================================================
 * THE "EDITED" MARKER IS NOT DECORATION
 * ============================================================================
 * A review thread is a record of a decision, and quoting somebody who has since
 * rewritten what they said is how those records go wrong. `editedAt` is the
 * server's own stamp (spec R5.4) and is surfaced next to the timestamp with the
 * edit time in its tooltip, so the original timestamp still reads as "when this
 * was said" and the marker answers "and it is not what was said then".
 *
 * Editing happens **in place**. A dialog would cover the pin, the screenshot,
 * and the rest of the conversation — everything the author is looking at while
 * they rewrite. Cancelling restores the stored body untouched, and a failed
 * save leaves the edit open with the new text still in it.
 *
 * Edit and delete are the comment author's, or an Admin's (spec matrix). The
 * menu is hidden for everyone else as a convenience; the API is the real check
 * (spec R7.8).
 */
export const CommentItem = ({
  comment,
  versionId,
  isFirst,
  onRequestDelete,
}: CommentItemProps) => {
  const [isEditing, setEditing] = useState(false);
  const updateComment = useUpdateComment();
  const can = useCan();
  const canModify = can.modifyComment(comment.author?.id ?? comment.authorId);

  const handleEdit = async (body: string): Promise<boolean> => {
    await updateComment.mutateAsync({
      versionId,
      commentId: comment.id,
      payload: toUpdateCommentPayload(body),
    });

    setEditing(false);
    return true;
  };

  return (
    <article className={cn('flex gap-2.5', isFirst ? '' : 'pl-1')}>
      <Avatar className="mt-0.5 size-6 shrink-0">
        <AvatarFallback className="bg-pin text-[10px] font-semibold text-pin-foreground">
          {authorInitial(comment.authorName)}
        </AvatarFallback>
      </Avatar>

      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-1.5">
          <span className="truncate text-xs font-medium text-foreground">
            {comment.authorName}
          </span>
          <time
            className="shrink-0 text-xs text-muted-foreground"
            dateTime={comment.createdAt}
            title={formatAbsoluteTime(comment.createdAt)}
          >
            {formatRelativeTime(comment.createdAt)}
          </time>
          {comment.editedAt === null ? null : (
            <span
              className="shrink-0 text-xs italic text-muted-foreground"
              title={`Edited ${formatAbsoluteTime(comment.editedAt)}`}
            >
              edited
            </span>
          )}

          {isEditing || !canModify ? null : (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button
                  type="button"
                  variant="ghost"
                  size="icon"
                  className="ml-auto size-6 shrink-0 text-muted-foreground hover:text-foreground"
                >
                  <MoreHorizontal aria-hidden="true" className="size-3.5" />
                  <span className="sr-only">
                    Actions for {comment.authorName}&rsquo;s comment
                  </span>
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-40">
                <DropdownMenuItem
                  onSelect={() => {
                    setEditing(true);
                  }}
                >
                  <Pencil aria-hidden="true" className="size-3.5" />
                  Edit
                </DropdownMenuItem>
                <DropdownMenuItem
                  className="text-destructive focus:text-destructive"
                  onSelect={() => {
                    onRequestDelete(comment);
                  }}
                >
                  <Trash2 aria-hidden="true" className="size-3.5" />
                  Delete
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          )}
        </div>

        {isEditing ? (
          <CommentComposer
            className="mt-1.5"
            label="Edit this comment"
            placeholder="Edit this comment"
            submitLabel="Save"
            pendingLabel="Saving…"
            defaultBody={comment.body}
            autoFocus
            rows={3}
            onSubmit={handleEdit}
            onCancel={() => {
              setEditing(false);
            }}
          />
        ) : (
          /*
           * `whitespace-pre-wrap`, because a comment is plain text (spec R5.3)
           * and the paragraphs somebody typed are part of what they wrote. It
           * is rendered as a text node, so it is escaped by React and no
           * uploaded prototype's markup can arrive through it.
           */
          <p className="mt-0.5 whitespace-pre-wrap break-words text-sm text-foreground">
            {comment.body}
          </p>
        )}
      </div>
    </article>
  );
};
